/**
 * ClientDetail — Client directory with detail panel
 * Zoho Books style: Left list + Right panel (Overview, Transactions, Statement tabs)
 * Mirrors VendorDetail with client-specific data (payments received, contract values).
 */
import { useEffect, useState, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast, Toaster } from 'sonner';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Search, User, Building2, Phone, Mail, FileText, DollarSign,
  Download, BarChart3, Activity, CreditCard, Briefcase, ArrowUpRight,
  ChevronDown, MoreHorizontal, Send, Ban, Pencil
} from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '@/components/ui/dropdown-menu';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import Layout from '@/components/Layout';

// ─── Left Panel: Client List ────────────────────────────────────────────
function ClientList({ clients, selectedId, onSelect, searchQuery, setSearchQuery }) {
  const filtered = useMemo(() => {
    if (!searchQuery) return clients;
    const q = searchQuery.toLowerCase();
    return clients.filter(c =>
      (c.name || '').toLowerCase().includes(q) ||
      (c.email || '').toLowerCase().includes(q) ||
      (c.company_name || '').toLowerCase().includes(q)
    );
  }, [clients, searchQuery]);

  return (
    <div className="w-[300px] min-w-[300px] bg-white border-r border-stone-200 flex flex-col h-[calc(100vh-64px)]" data-testid="client-list-panel">
      <div className="p-3 border-b border-stone-200">
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
          <Input
            placeholder="Search clients..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9 h-9 text-sm"
            data-testid="client-search-input"
          />
        </div>
        <p className="text-[10px] text-stone-400 mt-1.5">{filtered.length} clients</p>
      </div>
      <div className="flex-1 overflow-y-auto">
        {filtered.map(c => (
          <div
            key={c.id}
            onClick={() => onSelect(c.id)}
            className={`px-3 py-3 cursor-pointer border-b border-stone-100 transition-colors ${
              selectedId === c.id
                ? 'bg-stone-900 text-white'
                : 'hover:bg-stone-50'
            }`}
            data-testid={`client-item-${c.id}`}
          >
            <div className="flex items-center justify-between">
              <div className="min-w-0 flex-1">
                <p className={`font-medium text-sm truncate ${selectedId === c.id ? 'text-white' : 'text-stone-900'}`}>
                  {c.name}
                </p>
                {c.company_name && (
                  <p className={`text-xs truncate ${selectedId === c.id ? 'text-stone-300' : 'text-stone-500'}`}>
                    {c.company_name}
                  </p>
                )}
              </div>
              <div className="text-right ml-2 flex-shrink-0">
                {c.total_received > 0 && (
                  <p className={`text-xs font-semibold ${selectedId === c.id ? 'text-green-300' : 'text-stone-700'}`}>
                    {'\u20B9'}{c.total_received.toLocaleString('en-IN')}
                  </p>
                )}
                <p className={`text-[10px] ${selectedId === c.id ? 'text-stone-400' : 'text-stone-400'}`}>
                  {c.projects_count} project{c.projects_count !== 1 ? 's' : ''}
                </p>
              </div>
            </div>
          </div>
        ))}
        {filtered.length === 0 && (
          <div className="p-6 text-center text-sm text-stone-400">No clients found</div>
        )}
      </div>
    </div>
  );
}

// ─── Overview Tab ───────────────────────────────────────────────────────
function OverviewTab({ data }) {
  if (!data) return <div className="p-8 text-center text-stone-400">Loading...</div>;
  const { client, financial_summary, projects, recent_invoices, recent_quotations } = data;

  return (
    <div className="p-6 space-y-6" data-testid="client-overview-tab">
      {/* Top cards */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-stone-900 text-white rounded-lg p-4">
          <p className="text-xs text-stone-400 mb-1">Total Received</p>
          <p className="text-2xl font-bold">{'\u20B9'}{financial_summary.total_received.toLocaleString('en-IN')}</p>
          <p className="text-xs text-stone-400 mt-1">{financial_summary.project_count} project{financial_summary.project_count !== 1 ? 's' : ''}</p>
        </div>
        <div className="bg-blue-50 rounded-lg p-4 border border-blue-100">
          <p className="text-xs text-blue-600 mb-1">Contract Value</p>
          <p className="text-2xl font-bold text-blue-900">{'\u20B9'}{financial_summary.total_contract_value.toLocaleString('en-IN')}</p>
          <p className="text-xs text-blue-500 mt-1">Total across projects</p>
        </div>
        <div className={`rounded-lg p-4 border ${financial_summary.total_balance_due > 0 ? 'bg-amber-50 border-amber-200' : 'bg-green-50 border-green-100'}`}>
          <p className={`text-xs mb-1 ${financial_summary.total_balance_due > 0 ? 'text-amber-600' : 'text-green-600'}`}>Balance Due</p>
          <p className={`text-2xl font-bold ${financial_summary.total_balance_due > 0 ? 'text-amber-900' : 'text-green-900'}`}>
            {'\u20B9'}{financial_summary.total_balance_due.toLocaleString('en-IN')}
          </p>
          <p className={`text-xs mt-1 ${financial_summary.total_balance_due > 0 ? 'text-amber-500' : 'text-green-500'}`}>
            {financial_summary.total_balance_due > 0 ? 'Outstanding' : 'All paid'}
          </p>
        </div>
      </div>

      {/* Contact Details */}
      <div className="bg-white border border-stone-200 rounded-lg p-4">
        <h3 className="font-semibold text-stone-800 text-sm mb-3 flex items-center gap-2">
          <User size={14} /> Contact Details
        </h3>
        <div className="grid grid-cols-2 gap-x-8 gap-y-2.5 text-sm">
          <div className="flex items-center gap-2"><Mail size={13} className="text-stone-400" /><span className="text-stone-700">{client.email || '-'}</span></div>
          <div className="flex items-center gap-2"><Phone size={13} className="text-stone-400" /><span className="text-stone-700">{client.phone || 'Not provided'}</span></div>
          <div className="flex items-center gap-2"><Building2 size={13} className="text-stone-400" /><span className="text-stone-700">{client.company_name || 'Not provided'}</span></div>
          {client.gstin && <div className="flex items-center gap-2"><FileText size={13} className="text-stone-400" /><span className="text-stone-700">GSTIN: {client.gstin}</span></div>}
          {client.address && <div className="flex items-start gap-2 col-span-2"><FileText size={13} className="text-stone-400 mt-0.5" /><span className="text-stone-700">{client.address}</span></div>}
        </div>
      </div>

      {/* Projects */}
      {projects.length > 0 && (
        <div className="bg-white border border-stone-200 rounded-lg p-4">
          <h3 className="font-semibold text-stone-800 text-sm mb-3 flex items-center gap-2">
            <Briefcase size={14} /> Projects ({projects.length})
          </h3>
          <div className="space-y-2">
            {projects.map((p, i) => (
              <div key={i} className="flex items-center justify-between py-2.5 border-b border-stone-100 last:border-0">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-medium text-stone-800 truncate">{p.name}</p>
                    <Badge variant="outline" className={`text-[10px] ${
                      p.status === 'completed' ? 'bg-green-50 text-green-700 border-green-200' :
                      p.status === 'in_progress' ? 'bg-blue-50 text-blue-700 border-blue-200' :
                      'bg-stone-50 text-stone-600 border-stone-200'
                    }`}>{p.status?.replace(/_/g, ' ')}</Badge>
                  </div>
                  <p className="text-xs text-stone-500 mt-0.5">
                    Contract: {'\u20B9'}{(p.contract_value || 0).toLocaleString('en-IN')} | Received: {'\u20B9'}{(p.received || 0).toLocaleString('en-IN')}
                  </p>
                </div>
                <div className="text-right ml-3">
                  <p className={`text-sm font-semibold ${p.balance > 0 ? 'text-amber-700' : 'text-green-700'}`}>
                    {'\u20B9'}{(p.balance || 0).toLocaleString('en-IN')}
                  </p>
                  <p className="text-[10px] text-stone-400">balance</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recent Invoices */}
      {recent_invoices && recent_invoices.length > 0 && (
        <div className="bg-white border border-stone-200 rounded-lg p-4">
          <h3 className="font-semibold text-stone-800 text-sm mb-3 flex items-center gap-2">
            <FileText size={14} /> Recent Invoices
          </h3>
          <div className="space-y-2">
            {recent_invoices.map((inv, i) => (
              <div key={i} className="flex items-center justify-between py-2 border-b border-stone-100 last:border-0">
                <div>
                  <p className="text-sm text-stone-800">{inv.invoice_number || `Invoice #${(inv.id || '').slice(0, 8)}`}</p>
                  <p className="text-xs text-stone-500">{inv.date}</p>
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-sm text-stone-900">{'\u20B9'}{(inv.amount || 0).toLocaleString('en-IN')}</span>
                  <Badge variant="outline" className="text-[10px]">{inv.status}</Badge>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Transactions Tab ───────────────────────────────────────────────────
function TransactionsTab({ clientId }) {
  const token = localStorage.getItem('token');
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterProject, setFilterProject] = useState('all');
  const [projects, setProjects] = useState([]);

  useEffect(() => { fetchTransactions(); }, [clientId]);

  const fetchTransactions = async () => {
    setLoading(true);
    try {
      const resp = await axios.get(`${API}/clients/${clientId}/transactions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTransactions(resp.data.transactions || []);
      setProjects(resp.data.projects || []);
    } catch { toast.error('Failed to load transactions'); }
    finally { setLoading(false); }
  };

  const filtered = useMemo(() => {
    if (filterProject === 'all') return transactions;
    return transactions.filter(t => t.project_id === filterProject);
  }, [transactions, filterProject]);

  const filteredTotal = useMemo(() =>
    filtered.reduce((s, t) => s + (t.amount || 0), 0)
  , [filtered]);

  if (loading) return <div className="p-8 text-center text-stone-400">Loading transactions...</div>;

  return (
    <div className="p-6" data-testid="client-transactions-tab">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="font-semibold text-stone-800">All Payments Received</h3>
          <p className="text-xs text-stone-500">{filtered.length} transactions | Total: {'\u20B9'}{filteredTotal.toLocaleString('en-IN')}</p>
        </div>
        {projects.length > 1 && (
          <Select value={filterProject} onValueChange={setFilterProject}>
            <SelectTrigger className="w-48 h-8 text-xs" data-testid="client-txn-filter"><SelectValue placeholder="Filter by project" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Projects</SelectItem>
              {projects.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
      </div>

      {filtered.length > 0 ? (
        <div className="bg-white border border-stone-200 rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-stone-100 text-stone-600">
              <tr>
                <th className="p-3 text-left">Date</th>
                <th className="p-3 text-left">Project</th>
                <th className="p-3 text-left">Mode</th>
                <th className="p-3 text-left">Notes</th>
                <th className="p-3 text-right">Amount</th>
                <th className="p-3 text-center">Status</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((t, i) => (
                <tr key={t.id || i} className="border-b border-stone-100 hover:bg-stone-50" data-testid={`client-txn-row-${i}`}>
                  <td className="p-3 text-stone-700">{t.date}</td>
                  <td className="p-3 font-medium text-stone-800">{t.project_name || '-'}</td>
                  <td className="p-3 text-stone-600">
                    <span className="inline-flex items-center gap-1"><CreditCard size={12} />{t.mode || '-'}</span>
                  </td>
                  <td className="p-3 text-stone-500 max-w-[200px] truncate">{t.notes || '-'}</td>
                  <td className="p-3 text-right font-bold text-stone-900">{'\u20B9'}{(t.amount || 0).toLocaleString('en-IN')}</td>
                  <td className="p-3 text-center">
                    <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200 text-[10px]">Received</Badge>
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="bg-stone-800 text-white font-semibold">
                <td colSpan={4} className="p-3 text-right">Total Received:</td>
                <td className="p-3 text-right">{'\u20B9'}{filteredTotal.toLocaleString('en-IN')}</td>
                <td></td>
              </tr>
            </tfoot>
          </table>
        </div>
      ) : (
        <div className="bg-white border border-stone-200 rounded-lg p-10 text-center text-stone-400">
          <DollarSign size={32} className="mx-auto mb-2 text-stone-300" />
          No payments received from this client.
        </div>
      )}
    </div>
  );
}

// ─── Statement Tab ──────────────────────────────────────────────────────
function StatementTab({ clientId }) {
  const token = localStorage.getItem('token');
  const [statement, setStatement] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [dateRange, setDateRange] = useState('this_month');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const getDateRange = (preset) => {
    const now = new Date();
    let s, e;
    switch (preset) {
      case 'this_month':
        s = new Date(now.getFullYear(), now.getMonth(), 1); e = now; break;
      case 'last_month':
        s = new Date(now.getFullYear(), now.getMonth() - 1, 1); e = new Date(now.getFullYear(), now.getMonth(), 0); break;
      case 'this_quarter':
        const qm = Math.floor(now.getMonth() / 3) * 3;
        s = new Date(now.getFullYear(), qm, 1); e = now; break;
      case 'this_year':
        s = new Date(now.getFullYear(), 0, 1); e = now; break;
      case 'all_time':
        s = new Date(2020, 0, 1); e = now; break;
      default: return null;
    }
    return { start: s.toISOString().slice(0, 10), end: e.toISOString().slice(0, 10) };
  };

  const fetchStatement = async (sd, ed) => {
    setLoading(true);
    try {
      const resp = await axios.get(`${API}/clients/${clientId}/statement`, {
        params: { start_date: sd, end_date: ed },
        headers: { Authorization: `Bearer ${token}` }
      });
      setStatement(resp.data);
    } catch { toast.error('Failed to load statement'); }
    finally { setLoading(false); }
  };

  const handleRangeChange = (preset) => {
    setDateRange(preset);
    if (preset !== 'custom') {
      const range = getDateRange(preset);
      if (range) { setStartDate(range.start); setEndDate(range.end); fetchStatement(range.start, range.end); }
    }
  };

  useEffect(() => { handleRangeChange('this_month'); }, [clientId]);

  const downloadPDF = async () => {
    setDownloading(true);
    try {
      const resp = await axios.get(`${API}/clients/${clientId}/statement/pdf`, {
        params: { start_date: startDate, end_date: endDate },
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([resp.data]));
      const a = document.createElement('a'); a.href = url;
      a.download = `client_statement_${startDate}_to_${endDate}.pdf`;
      a.click(); window.URL.revokeObjectURL(url);
      toast.success('Statement downloaded');
    } catch { toast.error('Failed to download PDF'); }
    finally { setDownloading(false); }
  };

  return (
    <div className="p-6" data-testid="client-statement-tab">
      <div className="flex items-center gap-3 mb-6 flex-wrap">
        <Select value={dateRange} onValueChange={handleRangeChange}>
          <SelectTrigger className="w-40 h-9 text-sm" data-testid="client-statement-range"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="this_month">This Month</SelectItem>
            <SelectItem value="last_month">Last Month</SelectItem>
            <SelectItem value="this_quarter">This Quarter</SelectItem>
            <SelectItem value="this_year">This Year</SelectItem>
            <SelectItem value="all_time">All Time</SelectItem>
            <SelectItem value="custom">Custom Range</SelectItem>
          </SelectContent>
        </Select>
        {dateRange === 'custom' && (
          <>
            <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="w-36 h-9 text-sm" data-testid="client-stmt-start" />
            <span className="text-stone-400">to</span>
            <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="w-36 h-9 text-sm" data-testid="client-stmt-end" />
            <Button size="sm" onClick={() => { if (startDate && endDate) fetchStatement(startDate, endDate); }} data-testid="client-stmt-generate">Generate</Button>
          </>
        )}
        <div className="flex-1" />
        {statement && (
          <Button variant="outline" size="sm" onClick={downloadPDF} disabled={downloading} data-testid="client-download-statement-pdf">
            <Download size={14} className="mr-1" />
            {downloading ? 'Downloading...' : 'Download PDF'}
          </Button>
        )}
      </div>

      {loading && <div className="p-8 text-center text-stone-400">Loading statement...</div>}

      {statement && !loading && (
        <div className="space-y-6">
          {/* Account Summary */}
          <div className="bg-white border border-stone-200 rounded-lg overflow-hidden">
            <div className="bg-stone-900 text-white px-5 py-3">
              <h3 className="font-semibold">Account Summary</h3>
              <p className="text-xs text-stone-400">{statement.period.start} to {statement.period.end}</p>
            </div>
            <div className="grid grid-cols-4 divide-x divide-stone-200">
              <div className="p-4 text-center">
                <p className="text-xs text-stone-500 mb-1">Opening Balance</p>
                <p className="text-lg font-bold text-stone-800">{'\u20B9'}{statement.opening_balance.toLocaleString('en-IN')}</p>
              </div>
              <div className="p-4 text-center">
                <p className="text-xs text-stone-500 mb-1">Invoiced</p>
                <p className="text-lg font-bold text-blue-700">{'\u20B9'}{statement.total_debits.toLocaleString('en-IN')}</p>
              </div>
              <div className="p-4 text-center">
                <p className="text-xs text-stone-500 mb-1">Received</p>
                <p className="text-lg font-bold text-green-700">{'\u20B9'}{statement.total_credits.toLocaleString('en-IN')}</p>
              </div>
              <div className="p-4 text-center bg-stone-50">
                <p className="text-xs text-stone-500 mb-1">Closing Balance</p>
                <p className="text-lg font-bold text-stone-900">{'\u20B9'}{statement.closing_balance.toLocaleString('en-IN')}</p>
              </div>
            </div>
          </div>

          {/* Ledger Table */}
          <div className="bg-white border border-stone-200 rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-stone-100 text-stone-600">
                <tr>
                  <th className="p-3 text-left">Date</th>
                  <th className="p-3 text-left">Description</th>
                  <th className="p-3 text-left">Details</th>
                  <th className="p-3 text-right">Debit ({'\u20B9'})</th>
                  <th className="p-3 text-right">Credit ({'\u20B9'})</th>
                  <th className="p-3 text-right">Balance ({'\u20B9'})</th>
                </tr>
              </thead>
              <tbody>
                <tr className="bg-stone-50 border-b border-stone-200">
                  <td className="p-3 text-stone-500 italic" colSpan={3}>Opening Balance</td>
                  <td className="p-3 text-right">-</td>
                  <td className="p-3 text-right">-</td>
                  <td className="p-3 text-right font-semibold">{'\u20B9'}{statement.opening_balance.toLocaleString('en-IN')}</td>
                </tr>
                {statement.entries.map((e, i) => (
                  <tr key={i} className="border-b border-stone-100 hover:bg-stone-50" data-testid={`client-ledger-row-${i}`}>
                    <td className="p-3 text-stone-700 whitespace-nowrap">{e.date}</td>
                    <td className="p-3 font-medium text-stone-800">{e.description}</td>
                    <td className="p-3 text-stone-500">{e.details || '-'}</td>
                    <td className="p-3 text-right text-red-700 font-medium">{e.debit > 0 ? `${'\u20B9'}${e.debit.toLocaleString('en-IN')}` : '-'}</td>
                    <td className="p-3 text-right text-green-700 font-medium">{e.credit > 0 ? `${'\u20B9'}${e.credit.toLocaleString('en-IN')}` : '-'}</td>
                    <td className="p-3 text-right font-semibold text-stone-900">{'\u20B9'}{e.balance.toLocaleString('en-IN')}</td>
                  </tr>
                ))}
                {statement.entries.length === 0 && (
                  <tr><td colSpan={6} className="p-6 text-center text-stone-400">No transactions in this period</td></tr>
                )}
              </tbody>
              <tfoot>
                <tr className="bg-stone-800 text-white font-semibold">
                  <td colSpan={3} className="p-3 text-right">Closing Balance:</td>
                  <td className="p-3 text-right">{'\u20B9'}{statement.total_debits.toLocaleString('en-IN')}</td>
                  <td className="p-3 text-right">{'\u20B9'}{statement.total_credits.toLocaleString('en-IN')}</td>
                  <td className="p-3 text-right">{'\u20B9'}{statement.closing_balance.toLocaleString('en-IN')}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Edit Client Dialog ────────────────────────────────────────────────
const SALUTATIONS_C = ['Mr.', 'Mrs.', 'Ms.', 'Dr.', 'Prof.'];
const GST_TREATMENTS_C = ['Registered Business', 'Unregistered Business', 'Consumer', 'Overseas', 'SEZ', 'Deemed Export'];
const INDIAN_STATES_C = ['[AN] - Andaman & Nicobar', '[AP] - Andhra Pradesh', '[AR] - Arunachal Pradesh', '[AS] - Assam', '[BR] - Bihar', '[CG] - Chhattisgarh', '[CH] - Chandigarh', '[DD] - Daman & Diu', '[DL] - Delhi', '[GA] - Goa', '[GJ] - Gujarat', '[HP] - Himachal Pradesh', '[HR] - Haryana', '[JH] - Jharkhand', '[JK] - Jammu & Kashmir', '[KA] - Karnataka', '[KL] - Kerala', '[LA] - Ladakh', '[LD] - Lakshadweep', '[MH] - Maharashtra', '[ML] - Meghalaya', '[MN] - Manipur', '[MP] - Madhya Pradesh', '[MZ] - Mizoram', '[NL] - Nagaland', '[OD] - Odisha', '[PB] - Punjab', '[PY] - Puducherry', '[RJ] - Rajasthan', '[SK] - Sikkim', '[TN] - Tamil Nadu', '[TS] - Telangana', '[TR] - Tripura', '[UK] - Uttarakhand', '[UP] - Uttar Pradesh', '[WB] - West Bengal'];

function EditClientDialog({ open, onClose, client, onSaved }) {
  const token = localStorage.getItem('token');
  const [activeTab, setActiveTab] = useState('other_details');
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (client) {
      setForm({
        salutation: client.salutation || '',
        first_name: client.first_name || '',
        last_name: client.last_name || '',
        company_name: client.company_name || '',
        display_name: client.display_name || client.name || '',
        email: client.email || '',
        work_phone: client.work_phone || '',
        mobile: client.mobile || client.phone || '',
        // Other Details
        gst_treatment: client.gst_treatment || 'Consumer',
        place_of_supply: client.place_of_supply || '[MH] - Maharashtra',
        pan_number: client.pan_number || '',
        gstin: client.gstin || client.gst_number || '',
        // iter-386u-bk — preferred currency for BOQ PDFs to this client
        preferred_currency: client.preferred_currency || 'INR',
        // Address
        billing_address: client.billing_address || client.address || '',
        billing_city: client.billing_city || '',
        billing_state: client.billing_state || '',
        billing_pincode: client.billing_pincode || '',
        billing_country: client.billing_country || 'India',
        shipping_address: client.shipping_address || '',
        shipping_city: client.shipping_city || '',
        shipping_state: client.shipping_state || '',
        shipping_pincode: client.shipping_pincode || '',
        shipping_country: client.shipping_country || 'India',
        // Remarks
        remarks: client.remarks || client.notes || '',
      });
    }
  }, [client]);

  const set = (key, val) => setForm(p => ({ ...p, [key]: val }));

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = { ...form, name: form.display_name || `${form.first_name} ${form.last_name}`.trim() };
      await axios.put(`${API}/users/${client.id}`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Client updated');
      onSaved();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to update client');
    } finally { setSaving(false); }
  };

  const copyShipping = () => {
    set('shipping_address', form.billing_address);
    set('shipping_city', form.billing_city);
    set('shipping_state', form.billing_state);
    set('shipping_pincode', form.billing_pincode);
    set('shipping_country', form.billing_country);
    setForm(p => ({
      ...p,
      shipping_address: p.billing_address,
      shipping_city: p.billing_city,
      shipping_state: p.billing_state,
      shipping_pincode: p.billing_pincode,
      shipping_country: p.billing_country,
    }));
  };

  const tabs = [
    { id: 'other_details', label: 'Other Details' },
    { id: 'address', label: 'Address' },
    { id: 'remarks', label: 'Remarks' },
  ];

  const FieldRow = ({ label, required, children }) => (
    <div className="grid grid-cols-[130px_1fr] items-center gap-x-4">
      <Label className={`text-sm text-right ${required ? 'text-red-600 font-semibold' : 'text-stone-600'}`}>{label}{required ? ' *' : ''}</Label>
      {children}
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl w-[90vw] max-h-[90vh] overflow-y-auto" data-testid="edit-client-dialog">
        <DialogHeader>
          <DialogTitle className="text-lg">Edit Client</DialogTitle>
        </DialogHeader>

        {/* Top identity fields */}
        <div className="space-y-3 border-b border-stone-200 pb-4">
          <div className="grid grid-cols-[80px_1fr_1fr] gap-3">
            <div>
              <Label className="text-xs text-stone-500">Title</Label>
              <select value={form.salutation} onChange={e => set('salutation', e.target.value)} className="w-full border rounded px-2 py-1.5 text-sm bg-white">
                <option value="">–</option>
                {SALUTATIONS_C.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-xs text-stone-500">First Name</Label>
              <Input value={form.first_name || ''} onChange={e => set('first_name', e.target.value)} className="h-8" />
            </div>
            <div>
              <Label className="text-xs text-stone-500">Last Name</Label>
              <Input value={form.last_name || ''} onChange={e => set('last_name', e.target.value)} className="h-8" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-stone-500">Company Name</Label>
              <Input value={form.company_name || ''} onChange={e => set('company_name', e.target.value)} className="h-8" />
            </div>
            <div>
              <Label className="text-xs text-stone-500">Display Name *</Label>
              <Input value={form.display_name || ''} onChange={e => set('display_name', e.target.value)} className="h-8" data-testid="edit-client-display-name" />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label className="text-xs text-stone-500">Email</Label>
              <Input value={form.email || ''} onChange={e => set('email', e.target.value)} className="h-8" />
            </div>
            <div>
              <Label className="text-xs text-stone-500">Work Phone</Label>
              <Input value={form.work_phone || ''} onChange={e => set('work_phone', e.target.value)} className="h-8" />
            </div>
            <div>
              <Label className="text-xs text-stone-500">Mobile</Label>
              <Input value={form.mobile || ''} onChange={e => set('mobile', e.target.value)} className="h-8" />
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 border-b border-stone-200">
          {tabs.map(t => (
            <button key={t.id} onClick={() => setActiveTab(t.id)}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${activeTab === t.id ? 'border-stone-900 text-stone-900' : 'border-transparent text-stone-500 hover:text-stone-700'}`}
              data-testid={`edit-client-tab-${t.id}`}
            >{t.label}</button>
          ))}
        </div>

        <div className="min-h-[280px] py-3 space-y-3">
          {activeTab === 'other_details' && (
            <>
              <FieldRow label="GST Treatment">
                <select value={form.gst_treatment || ''} onChange={e => set('gst_treatment', e.target.value)} className="border rounded px-2 py-1.5 text-sm bg-white w-full">
                  {GST_TREATMENTS_C.map(g => <option key={g} value={g}>{g}</option>)}
                </select>
              </FieldRow>
              <FieldRow label="Place of Supply">
                <select value={form.place_of_supply || ''} onChange={e => set('place_of_supply', e.target.value)} className="border rounded px-2 py-1.5 text-sm bg-white w-full">
                  {INDIAN_STATES_C.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </FieldRow>
              <FieldRow label="PAN">
                <Input value={form.pan_number || ''} onChange={e => set('pan_number', e.target.value)} className="h-8" placeholder="ABCDE1234F" />
              </FieldRow>
              <FieldRow label="GSTIN">
                <Input value={form.gstin || ''} onChange={e => set('gstin', e.target.value)} className="h-8" placeholder="22AAAAA0000A1Z5" />
              </FieldRow>
              <FieldRow label="Preferred Currency">
                <select
                  value={form.preferred_currency || 'INR'}
                  onChange={e => set('preferred_currency', e.target.value)}
                  className="border rounded px-2 py-1.5 text-sm bg-white w-full"
                  data-testid="edit-client-preferred-currency"
                >
                  <option value="INR">₹ Indian Rupee (INR)</option>
                  <option value="USD">$ US Dollar (USD)</option>
                  <option value="GBP">£ British Pound (GBP)</option>
                  <option value="EUR">€ Euro (EUR)</option>
                  <option value="AED">د.إ UAE Dirham (AED)</option>
                </select>
              </FieldRow>
            </>
          )}

          {activeTab === 'address' && (
            <div className="grid grid-cols-2 gap-6">
              <div className="space-y-3">
                <h4 className="font-semibold text-sm text-stone-800">Billing Address</h4>
                <div>
                  <Label className="text-xs text-stone-500">Street Address</Label>
                  <Textarea value={form.billing_address || ''} onChange={e => set('billing_address', e.target.value)} rows={2} className="text-sm" data-testid="edit-client-billing-address" />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs text-stone-500">City</Label>
                    <Input value={form.billing_city || ''} onChange={e => set('billing_city', e.target.value)} className="h-8" />
                  </div>
                  <div>
                    <Label className="text-xs text-stone-500">State</Label>
                    <select value={form.billing_state || ''} onChange={e => set('billing_state', e.target.value)} className="border rounded px-2 py-1.5 text-sm bg-white w-full">
                      <option value="">Select</option>
                      {INDIAN_STATES_C.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs text-stone-500">Pincode</Label>
                    <Input value={form.billing_pincode || ''} onChange={e => set('billing_pincode', e.target.value)} className="h-8" />
                  </div>
                  <div>
                    <Label className="text-xs text-stone-500">Country</Label>
                    <Input value={form.billing_country || 'India'} onChange={e => set('billing_country', e.target.value)} className="h-8" />
                  </div>
                </div>
              </div>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <h4 className="font-semibold text-sm text-stone-800">Shipping Address</h4>
                  <Button variant="link" size="sm" onClick={copyShipping} className="text-xs text-blue-600 h-auto p-0" data-testid="copy-billing-to-shipping">
                    Copy from Billing
                  </Button>
                </div>
                <div>
                  <Label className="text-xs text-stone-500">Street Address</Label>
                  <Textarea value={form.shipping_address || ''} onChange={e => set('shipping_address', e.target.value)} rows={2} className="text-sm" />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs text-stone-500">City</Label>
                    <Input value={form.shipping_city || ''} onChange={e => set('shipping_city', e.target.value)} className="h-8" />
                  </div>
                  <div>
                    <Label className="text-xs text-stone-500">State</Label>
                    <select value={form.shipping_state || ''} onChange={e => set('shipping_state', e.target.value)} className="border rounded px-2 py-1.5 text-sm bg-white w-full">
                      <option value="">Select</option>
                      {INDIAN_STATES_C.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs text-stone-500">Pincode</Label>
                    <Input value={form.shipping_pincode || ''} onChange={e => set('shipping_pincode', e.target.value)} className="h-8" />
                  </div>
                  <div>
                    <Label className="text-xs text-stone-500">Country</Label>
                    <Input value={form.shipping_country || 'India'} onChange={e => set('shipping_country', e.target.value)} className="h-8" />
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'remarks' && (
            <div>
              <Label className="text-xs text-stone-500 mb-1 block">Remarks / Notes</Label>
              <Textarea value={form.remarks || ''} onChange={e => set('remarks', e.target.value)} rows={5} placeholder="Add notes about this client..." className="text-sm" data-testid="edit-client-remarks" />
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="save-client-btn">
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Main Component ─────────────────────────────────────────────────────
export default function ClientDetail() {
  const { clientId: urlClientId } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  const [clients, setClients] = useState([]);
  const [selectedClientId, setSelectedClientId] = useState(urlClientId || null);
  const [overview, setOverview] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);

  useEffect(() => { fetchClients(); }, []);

  useEffect(() => {
    if (selectedClientId) {
      fetchOverview(selectedClientId);
      navigate(`/clients-directory/${selectedClientId}`, { replace: true });
    }
  }, [selectedClientId]);

  const fetchClients = async () => {
    try {
      const resp = await axios.get(`${API}/clients/directory`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setClients(resp.data || []);
      if (!selectedClientId && resp.data.length > 0) {
        setSelectedClientId(urlClientId || resp.data[0].id);
      }
    } catch { toast.error('Failed to load clients'); }
    finally { setLoading(false); }
  };

  const fetchOverview = async (cid) => {
    try {
      const resp = await axios.get(`${API}/clients/${cid}/overview`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOverview(resp.data);
    } catch { setOverview(null); }
  };

  const handleSelectClient = (cid) => {
    setSelectedClientId(cid);
    setActiveTab('overview');
    setOverview(null);
  };

  const handleEmailClient = () => {
    if (overview?.client?.email) {
      window.open(`mailto:${overview.client.email}`, '_blank');
    } else {
      toast.error('No email address for this client');
    }
  };

  const tabs = [
    { key: 'overview', label: 'Overview', icon: BarChart3 },
    { key: 'transactions', label: 'Transactions', icon: DollarSign },
    { key: 'statement', label: 'Ledger / Statement', icon: FileText },
  ];

  if (loading) {
    return <Layout><div className="flex items-center justify-center h-[calc(100vh-64px)] bg-stone-50"><p className="text-stone-400">Loading client directory...</p></div></Layout>;
  }

  return (
    <Layout>
    <div className="flex h-[calc(100vh-64px)] bg-stone-50" data-testid="client-detail-page">
      <Toaster position="top-right" richColors />

      <ClientList
        clients={clients}
        selectedId={selectedClientId}
        onSelect={handleSelectClient}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
      />

      <div className="flex-1 flex flex-col overflow-hidden">
        {selectedClientId && overview ? (
          <>
            {/* Client Header */}
            <div className="bg-white border-b border-stone-200 px-6 py-4 flex-shrink-0">
              <div className="flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    <h2 className="text-xl font-bold text-stone-900" data-testid="client-detail-name">{overview.client.name}</h2>
                    {overview.client.status === 'inactive' && (
                      <Badge variant="outline" className="bg-red-50 text-red-600 border-red-200 text-[10px]">Inactive</Badge>
                    )}
                  </div>
                  <p className="text-sm text-stone-500">{overview.client.company_name || overview.client.email}</p>
                </div>

                <div className="flex items-center gap-2" data-testid="client-action-buttons">
                  <Button variant="outline" size="sm" onClick={() => setEditOpen(true)} data-testid="edit-client-btn">
                    <Pencil size={14} className="mr-1" /> Edit
                  </Button>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="outline" size="sm" data-testid="client-more-btn">
                        Actions <ChevronDown size={12} className="ml-1" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-48">
                      <DropdownMenuItem onClick={handleEmailClient} data-testid="action-email-client">
                        <Send size={14} className="mr-2" /> Email Client
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>

              {/* Tabs */}
              <div className="flex gap-1 mt-4 border-b border-stone-200 -mb-[17px]">
                {tabs.map(t => {
                  const Icon = t.icon;
                  return (
                    <button
                      key={t.key}
                      onClick={() => setActiveTab(t.key)}
                      className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                        activeTab === t.key
                          ? 'border-stone-900 text-stone-900'
                          : 'border-transparent text-stone-500 hover:text-stone-700'
                      }`}
                      data-testid={`client-tab-${t.key}`}
                    >
                      <Icon size={14} />
                      {t.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Tab Content */}
            <div className="flex-1 overflow-y-auto">
              {activeTab === 'overview' && <OverviewTab data={overview} />}
              {activeTab === 'transactions' && <TransactionsTab clientId={selectedClientId} />}
              {activeTab === 'statement' && <StatementTab clientId={selectedClientId} />}
            </div>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center text-stone-400">
              <User size={48} className="mx-auto mb-3 text-stone-300" />
              <p className="font-medium">Select a client to view details</p>
              <p className="text-sm mt-1">Choose from the list on the left</p>
            </div>
          </div>
        )}
      </div>
    </div>
    
    {/* Edit Client Dialog */}
    {overview?.client && (
      <EditClientDialog
        open={editOpen}
        onClose={() => setEditOpen(false)}
        client={overview.client}
        onSaved={fetchClients}
      />
    )}
    </Layout>
  );
}
